// Flow inference for the refill wizard. The wizard's 8 underlying steps model
// the full mix-a-batch → pour-into-reservoir pipeline, but a given session may
// only need a subset (e.g. a direct-to-reservoir dose skips the mixing batch's
// proposed-mix / pre-pour / pour steps). Rather than ask the user up front what
// kind of fill they're doing, we INFER the applicable steps from what they've
// actually done so far.
//
// These helpers are pure so they can be unit-tested in isolation; the wizard
// computes them from the live session + steps on every render.

import type { RefillSession, RefillStep } from "@/lib/api/types";

// Underlying step numbers (kept stable so `?step=N` and the route's
// validateSearch are unchanged). applicableSteps() returns the subset that's
// relevant for a given session.
//   1 Current state      — always
//   2 AI analysis        — always
//   3 Prioritized        — always
//   4 Proposed mix       — ONLY if hasRecipe
//   5 Dose               — always
//   6 Pre-pour read      — ONLY if hasMixingBatch
//   7 Pour               — ONLY if hasMixingBatch
//   8 Post-pour & close  — always
export const ALL_STEPS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

// A session "has a mixing batch" iff a batch size has been set (> 0) OR any
// ACTIVE step (not voided, not superseded) was recorded against the
// mixing_batch vessel. Either signal means the user is mixing a separate batch
// (vs. dosing the reservoir directly), so the pre-pour + pour steps apply.
export function hasMixingBatch(
  session: Pick<RefillSession, "batch_size_gal">,
  steps: Pick<RefillStep, "target" | "voided_at" | "superseded_by_id">[],
): boolean {
  if ((session.batch_size_gal ?? 0) > 0) return true;
  return steps.some(
    (s) =>
      s.target === "mixing_batch" && !s.voided_at && !s.superseded_by_id,
  );
}

// hasRecipe — a recipe is pinned to the session, so the proposed-mix step (4)
// applies (the engine can order + size the recipe).
export function hasRecipe(session: Pick<RefillSession, "recipe_id">): boolean {
  return !!session.recipe_id;
}

// applicableSteps returns the ordered list of APPLICABLE underlying step
// numbers (a subset of 1..8) for the given session + steps.
export function applicableSteps(
  session: Pick<RefillSession, "recipe_id" | "batch_size_gal">,
  steps: Pick<RefillStep, "target" | "voided_at" | "superseded_by_id">[],
): number[] {
  const recipe = hasRecipe(session);
  const batch = hasMixingBatch(session, steps);
  return ALL_STEPS.filter((n) => {
    switch (n) {
      case 4:
        return recipe;
      case 6:
      case 7:
        return batch;
      default:
        return true;
    }
  });
}

// mixStepAdvanceTarget decides where the mix step's "Done dosing →" button
// should navigate. The mix step lets the user flip a local vessel toggle to
// "mixing_batch" to signal INTENT to mix a separate batch — which would surface
// the pre-pour (6) + pour (7) steps. But that intent only widens the applicable
// list once it's actually PERSISTED (a batch size > 0, or a recorded
// mixing_batch step); see hasMixingBatch. If we advanced against a *synthesized*
// batch that isn't persisted, the page would recompute its real applicable list
// (which omits 6,7) and nearestApplicableStep would snap the user FORWARD past
// pre-pour/pour — silently skipping them.
//
// So we advance against the session's REAL applicable list (the same list the
// page derives), never a synthesized one. The invariant this guarantees: the
// returned step is ALWAYS in applicableSteps(session, steps), so the page-level
// redirect can never bounce the user past a step they intended to visit.
//
// `intendsMixingBatch` is the live dose-card toggle. When the toggle is on but
// the batch isn't persisted yet (no batch_size_gal, no recorded mixing step),
// canAdvanceFromMixStep is false — the UI should keep the user on the mix step
// (e.g. disable "Done dosing →") until they persist the batch, rather than
// advancing them into a path that immediately collapses.
export function mixStepAdvanceTarget(
  current: number,
  session: Pick<RefillSession, "recipe_id" | "batch_size_gal">,
  steps: Pick<RefillStep, "target" | "voided_at" | "superseded_by_id">[],
): number {
  return nextApplicableStep(current, applicableSteps(session, steps));
}

// canAdvanceFromMixStep encodes the gate for the mix step's "Done dosing →"
// button. Advancing is only safe when it won't drop the user past a step they
// intend to visit:
//   • toggle in "reservoir" (direct dose): always fine — there are no pre-pour/
//     pour steps to skip, so advancing goes straight to post-pour.
//   • toggle in "mixing_batch" (intent to mix a separate batch): only safe once
//     that intent is PERSISTED (batch_size_gal > 0 or a recorded mixing_batch
//     step). Until then the real applicable list omits 6,7, so advancing would
//     snap past them — block it and prompt the user to set a batch size.
export function canAdvanceFromMixStep(
  intendsMixingBatch: boolean,
  session: Pick<RefillSession, "batch_size_gal">,
  steps: Pick<RefillStep, "target" | "voided_at" | "superseded_by_id">[],
): boolean {
  if (!intendsMixingBatch) return true;
  return hasMixingBatch(session, steps);
}

// nextApplicableStep returns the first applicable step strictly after `current`
// (in the underlying numbering). If `current` is at/after the last applicable
// step, it returns the last applicable step (clamped — there's nowhere further
// to go). If the applicable list is empty, returns `current` unchanged.
export function nextApplicableStep(
  current: number,
  applicable: number[],
): number {
  if (applicable.length === 0) return current;
  const next = applicable.find((n) => n > current);
  return next ?? applicable[applicable.length - 1]!;
}

// prevApplicableStep returns the last applicable step strictly before
// `current`. If `current` is at/before the first applicable step, it returns
// the first applicable step (clamped). If the applicable list is empty, returns
// `current` unchanged.
export function prevApplicableStep(
  current: number,
  applicable: number[],
): number {
  if (applicable.length === 0) return current;
  let prev: number | undefined;
  for (const n of applicable) {
    if (n < current) prev = n;
    else break;
  }
  return prev ?? applicable[0]!;
}

// nearestApplicableStep snaps an arbitrary step (e.g. a hand-edited URL that
// landed on a now-hidden step) to the closest applicable step, preferring the
// next applicable step forward, else the previous one.
export function nearestApplicableStep(
  current: number,
  applicable: number[],
): number {
  if (applicable.length === 0) return current;
  if (applicable.includes(current)) return current;
  const next = applicable.find((n) => n > current);
  if (next !== undefined) return next;
  return applicable[applicable.length - 1]!;
}
